'use client'

// RegionGeo abstraction (Overmind→server-props migration, Phase C2 part 2).
//
// WHAT THIS IS
// The dedicated seam that supplies the REGION-DETERMINISTIC geo values the SSR
// HTML needs — the region SHIPPING INFO (`shippingInfo`: ships-from / transit /
// free-shipping) and the SSR pricing COUNTRY (`newRegion`: the country code the
// page priced against) — to the in-page consumers, sourced from SERVER PROPS
// threaded through RegionPageClient, NOT from a render-time Overmind
// `state.shippingInfo` / `state.newRegion` read. It mirrors components/NamedPrices,
// components/RegionSurfaces and components/RegionOffers: a `'use client'` context
// Provider + accessor hooks.
//
// WHY THESE TWO ARE SERVER PROPS (and countries/shippingCountry/served are NOT)
// `shippingInfo` IS in the SSR region price bundle (getRegionPriceBundle ->
// bundle.shippingInfo, hydrated into state.shippingInfo by buildRegionMutations)
// and `newRegion` is purely URL-region-derived (regionToFetchCountry) — both are
// region-deterministic (same for every visitor to /us|/eu|/au|/world) and belong
// in the shared-cacheable SSR HTML. `countries` (post-mount client fetch) and
// `shippingCountry`/`served` (per-user cookie/geo overlay) are per-visitor and
// live in the client store/regionStore.js instead.
//
// DATA SOURCE + HYDRATION SAFETY
// lib/regionPageData threads `shippingInfo` (= bundle.shippingInfo) and `newRegion`
// (= regionToFetchCountry(region) on a commerce page, null on a content page) as
// props → page.js → RegionPageClient → <RegionGeoProvider>. These are the SAME
// values buildRegionMutations hydrates into state.shippingInfo / state.newRegion,
// so at SSR + first client render the provider value === the hydrated store value
// → the consumer render is hydration-safe (no React #418/#423).
//
// POST-MOUNT (why the hooks still touch the region store)
//   - shippingInfo: the sub-region refinement (hooks/useRegionChange
//     refineForShippingCountry — FR/IT on /eu) and the in-place payment-page region
//     change now write the Zustand REGION STORE's shippingInfo (Phase D2, formerly
//     Overmind state.shippingInfo). useRegionShippingInfo() therefore prefers the
//     region-store value when it carries content and falls back to the server prop
//     when the store is at its empty default (no refinement / unit test).
//   - newRegion: the client store (store/regionStore.js) resolves/updates it
//     post-mount (geo-detected on content pages, the in-place payment-page region
//     change). useRegionCountryCode() prefers the resolved store value and falls
//     back to the server prop (SSR).
//
// APP-ROUTER SAFETY
// A `'use client'` boundary (React context + the Zustand region store). It is
// imported ONLY by client components. No next/router, no window at render.

import React, { createContext, useContext } from 'react'
import { useRegionStore } from '../../store/regionStore'

// Server-threaded { shippingInfo, newRegion }, or null when no provider is present.
const RegionGeoContext = createContext(null)

export function RegionGeoProvider({ shippingInfo = null, newRegion = null, children }) {
  return (
    <RegionGeoContext.Provider value={{ shippingInfo, newRegion }}>
      {children}
    </RegionGeoContext.Provider>
  )
}

// True when a shippingInfo object carries any real content (vs the empty default
// `{ shipsFrom:'', transitTimes:'', transitTimeDays:'' }`).
function hasShippingInfoContent(si) {
  return !!(
    si &&
    (si.shipsFrom ||
      si.transitTimeDays ||
      si.transitTimes ||
      si.processingTimeDays ||
      si.freeShipping != null)
  )
}

/**
 * The region shipping info (ships-from / transit / free-shipping). Prefers the
 * post-mount REFINED region-store value (the sub-region overlay / in-place region
 * change) when it carries content; falls back to the server prop when the store is
 * at its empty default (no refinement / unit test). Always returns an object so
 * callers can read `.freeShipping` / `.transitTimeDays` without a guard.
 * @returns {any}
 */
export function useRegionShippingInfo() {
  const ctx = useContext(RegionGeoContext)
  const storeShippingInfo = useRegionStore((s) => s.shippingInfo)
  const serverShippingInfo = ctx ? ctx.shippingInfo : null
  if (hasShippingInfoContent(storeShippingInfo)) {
    return storeShippingInfo
  }
  return serverShippingInfo || storeShippingInfo || {}
}

/**
 * The country code the page prices against (US/DE/AU/…). Prefers the client-
 * resolved region-store value (geo-detected on content pages, updated on the
 * in-place payment-page region change), else the SSR server prop.
 * @returns {string|null}
 */
export function useRegionCountryCode() {
  const ctx = useContext(RegionGeoContext)
  const storeNewRegion = useRegionStore((s) => s.newRegion)
  const serverNewRegion = ctx ? ctx.newRegion : null
  return storeNewRegion || serverNewRegion || null
}

export { RegionGeoContext }
